import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * La pill di vetro della sidebar: scivola sulla voce attiva e ne prende la
 * misura (sulle sottovoci e' piu' stretta e piu' bassa, rientrata come loro).
 *
 * Tutto il movimento e' su `transform` (Web Animations API), quindi gira sul
 * compositor: se la pagina nuova tiene occupato il main thread mentre si
 * monta, la pill non si ferma. Tecnica FLIP: la pill prende subito larghezza,
 * altezza e posizione finali e un'animazione la porta dal punto in cui si
 * trova adesso (anche a meta' di un altro movimento) a quello finale.
 *
 * Il punto finale tiene conto delle sottovoci che si stanno aprendo o
 * chiudendo (`[data-sb-sub]`, accordion su grid-template-rows): la voce di
 * destinazione si misura dove SARA' a fine accordion, cosi' la pill non arriva
 * nel punto sbagliato e non deve rincorrere niente.
 *
 * La "gelatina" (la firma che piace a Lorenzo) vive sul figlio-vetro: una
 * molla smorzata campionata in fotogrammi chiave, proporzionale alla distanza,
 * che parte dalla deformazione in corso se si clicca mentre la pill si muove
 * (niente scatti). Niente movimento con prefers-reduced-motion.
 */

/** Curva dello scivolamento: la stessa dell'accordion delle sottovoci (--sb-ease). */
const EASE_SLIDE = 'cubic-bezier(0.22, 1, 0.36, 1)';

type Box = { x: number; y: number; w: number; h: number };

/** Rettangolo di layout rispetto al padding box di `root` (ignora i transform: :active, entrate). */
function layoutBox(el: HTMLElement, root: HTMLElement): Box | null {
  let x = 0;
  let y = 0;
  let n: HTMLElement | null = el;
  while (n && n !== root) {
    x += n.offsetLeft;
    y += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  if (n !== root) return null;
  return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}

/** Dove sara' la voce a fine accordion: somma quanto devono ancora crescere/calare le sottovoci sopra di lei. */
function finalBox(target: HTMLElement, nav: HTMLElement): Box | null {
  const b = layoutBox(target, nav);
  if (!b) return null;
  nav.querySelectorAll<HTMLElement>('[data-sb-sub]').forEach((c) => {
    if (c.contains(target)) return;
    const cb = layoutBox(c, nav);
    if (!cb || cb.y > b.y) return;
    const list = c.querySelector<HTMLElement>('.sb-sub-list');
    const end = c.dataset.open === 'true' && list ? list.offsetHeight : 0;
    b.y += end - c.offsetHeight;
  });
  return b;
}

/** Rettangolo visivo attuale della pill (compreso lo scivolamento in corso). */
function visualBox(pill: HTMLElement, nav: HTMLElement): Box {
  const n = nav.getBoundingClientRect();
  const r = pill.getBoundingClientRect();
  return { x: r.left - n.left - nav.clientLeft + nav.scrollLeft, y: r.top - n.top - nav.clientTop + nav.scrollTop, w: r.width, h: r.height };
}

/** Deformazione attuale del vetro: matrix(a, b, c, d, e, f) -> scaleX = a, scaleY = d, ty = f. */
function glassNow(glass: HTMLElement): { sx: number; sy: number; ty: number } {
  const m = getComputedStyle(glass).transform;
  const v = m && m.startsWith('matrix(') ? m.slice(7, -1).split(',').map(Number) : [];
  const [sx, , , sy, , ty] = v;
  return v.length === 6 && [sx, sy, ty].every((n) => Number.isFinite(n))
    ? { sx: sx as number, sy: sy as number, ty: ty as number }
    : { sx: 1, sy: 1, ty: 0 };
}

/**
 * Molla smorzata normalizzata (picco = 1 a ~10% della durata, poi la
 * "schiacciata" a ~36% e un'ultima onda quasi invisibile), sfumata a 0 in coda.
 */
const ZETA = 0.375;
const OMEGA_D = 12;
const OMEGA = OMEGA_D / Math.sqrt(1 - ZETA * ZETA);
const PEAK = Math.exp(-ZETA * OMEGA * (Math.atan(Math.sqrt(1 - ZETA * ZETA) / ZETA) / OMEGA_D)) * Math.sin(Math.atan(Math.sqrt(1 - ZETA * ZETA) / ZETA));
function spring(t: number) {
  const k = (Math.exp(-ZETA * OMEGA * t) * Math.sin(OMEGA_D * t)) / PEAK;
  const tail = t < 0.78 ? 1 : 1 - smooth((t - 0.78) / 0.22);
  return k * tail;
}
const smooth = (u: number) => u * u * (3 - 2 * u);

const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export function useSidebarPill(
  navRef: RefObject<HTMLElement>,
  pillRef: RefObject<HTMLElement>,
  glassRef: RefObject<HTMLElement>,
  /** Cambia quando cambia la voce attiva: la pill scivola. */
  activeKey: string,
  /** Cambia quando cambia la geometria della sidebar (compressa/espansa): la pill si riposiziona senza scivolare. */
  layoutKey: string,
) {
  const shown = useRef(false);
  const slide = useRef<Animation | null>(null);
  const jelly = useRef<Animation | null>(null);
  const prevLayout = useRef(layoutKey);

  const target = () => {
    const nav = navRef.current;
    if (!nav) return null;
    const actives = nav.querySelectorAll<HTMLElement>('[aria-current="true"]');
    return actives[actives.length - 1] ?? null; // la piu' profonda (sottovoce se c'e')
  };

  const place = (pill: HTMLElement, b: Box) => {
    pill.style.width = `${b.w}px`;
    pill.style.height = `${b.h}px`;
    pill.style.transform = `translate(${b.x}px, ${b.y}px)`;
  };

  const hide = (pill: HTMLElement) => {
    shown.current = false;
    pill.style.opacity = '0';
  };

  /** Senza movimento: primo disegno, sidebar che cambia larghezza, finestra ridimensionata. */
  const snap = () => {
    const nav = navRef.current;
    const pill = pillRef.current;
    if (!nav || !pill) return;
    const t = target();
    const b = t && finalBox(t, nav);
    if (!b) { hide(pill); return; }
    slide.current?.cancel();
    jelly.current?.cancel();
    place(pill, b);
    pill.style.opacity = '1';
    shown.current = true;
  };

  const move = () => {
    const nav = navRef.current;
    const pill = pillRef.current;
    const glass = glassRef.current;
    if (!nav || !pill || !glass) return;
    const t = target();
    const to = t && finalBox(t, nav);
    if (!to) { hide(pill); return; }
    if (!shown.current || reduceMotion() || typeof pill.animate !== 'function') { snap(); return; }

    // Punto di partenza: dove la pill E' adesso, anche a meta' di un movimento
    const from = visualBox(pill, nav);
    const g0 = glassNow(glass);
    slide.current?.cancel();
    jelly.current?.cancel();
    place(pill, to);

    const dy = to.y - from.y;
    const dist = Math.hypot(to.x - from.x, dy);
    const sameSize = Math.abs(from.w - to.w) < 0.5 && Math.abs(from.h - to.h) < 0.5;
    if (dist < 0.5 && sameSize && Math.abs(g0.sy - 1) < 0.002) return;

    const k = Math.min(1, dist / 480);                 // 0 = adiacente, 1 = salto lungo
    const duration = Math.round(320 + 260 * k);        // slide piu' lunga per salti piu' lunghi
    slide.current = pill.animate(
      [
        { transform: `translate(${from.x}px, ${from.y}px) scale(${from.w / to.w}, ${from.h / to.h})` },
        { transform: `translate(${to.x}px, ${to.y}px)` },
      ],
      { duration, easing: EASE_SLIDE },
    );

    // Gelatina: si allunga nella direzione del viaggio (il bordo davanti va
    // avanti, quello dietro resta), si schiaccia arrivando e si assesta.
    const stretch = Math.abs(dy) < 1 ? 0 : 0.05 + 0.13 * k;
    const squeeze = Math.abs(dy) < 1 ? 0 : 0.012 + 0.03 * k;
    const dir = dy >= 0 ? 1 : -1;
    const N = 28;
    const frames: Keyframe[] = [];
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      const s = spring(u);
      const rest = Math.pow(1 - u, 3);                 // la deformazione che c'era sfuma via in fretta
      const sy = 1 + stretch * s + (g0.sy - 1) * rest;
      const sx = 1 - squeeze * s + (g0.sx - 1) * rest;
      const ty = dir * stretch * s * (to.h / 2) + g0.ty * rest;
      frames.push({ transform: `translate(0px, ${ty.toFixed(3)}px) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})` });
    }
    jelly.current = glass.animate(frames, { duration, easing: 'linear' });
    watch(slide.current, to);
  };

  /**
   * Correzione senza gelatina: la voce si e' spostata mentre la pill correva
   * (compare la barra di scorrimento quando le sottovoci si aprono, un
   * conteggio cambia larghezza...). Riparte da dove la pill e' adesso e arriva
   * nel tempo che restava, cosi' sembra lo stesso movimento.
   */
  const retarget = () => {
    const nav = navRef.current;
    const pill = pillRef.current;
    if (!nav || !pill) return;
    if (!shown.current || reduceMotion() || typeof pill.animate !== 'function') { snap(); return; }
    const t = target();
    const to = t && finalBox(t, nav);
    if (!to) { hide(pill); return; }
    const from = visualBox(pill, nav);
    const running = slide.current && slide.current.playState === 'running' ? slide.current : null;
    if (!running && Math.abs(from.x - to.x) < 0.5 && Math.abs(from.y - to.y) < 0.5 && Math.abs(from.w - to.w) < 0.5 && Math.abs(from.h - to.h) < 0.5) return;
    const timing = running?.effect?.getComputedTiming();
    const left = timing ? Number(timing.endTime ?? 0) - Number(timing.localTime ?? 0) : 0;
    running?.cancel();
    place(pill, to);
    slide.current = pill.animate(
      [
        { transform: `translate(${from.x}px, ${from.y}px) scale(${from.w / to.w}, ${from.h / to.h})` },
        { transform: `translate(${to.x}px, ${to.y}px)` },
      ],
      { duration: Math.max(180, left), easing: 'cubic-bezier(0.33, 1, 0.68, 1)' },
    );
    watch(slide.current, to);
  };

  /** A fine corsa: se la voce non e' dove la pill e' arrivata, ci si rimette sopra (scivolando). */
  const watch = (anim: Animation, to: Box) => {
    anim.finished.then(() => {
      if (slide.current !== anim) return;
      const nav = navRef.current;
      const t = target();
      const now = t && nav && finalBox(t, nav);
      if (!now) return;
      const off = Math.abs(now.y - to.y) > 0.5 || Math.abs(now.x - to.x) > 0.5 || Math.abs(now.w - to.w) > 0.5 || Math.abs(now.h - to.h) > 0.5;
      if (off) retarget();
    }).catch(() => { /* annullata da un movimento nuovo */ });
  };

  useLayoutEffect(() => {
    const layoutChanged = prevLayout.current !== layoutKey;
    prevLayout.current = layoutKey;
    if (layoutChanged) snap(); else move();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, layoutKey]);

  // La sidebar cambia misura (compressa/espansa, finestra, barra di scorrimento
  // che compare): da ferma la pill si rimette a posto, in corsa corregge la rotta
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    let w = nav.clientWidth;
    let h = nav.clientHeight;
    const ro = new ResizeObserver(() => {
      if (nav.clientWidth === w && nav.clientHeight === h) return;
      w = nav.clientWidth;
      h = nav.clientHeight;
      retarget();
    });
    ro.observe(nav);
    return () => { ro.disconnect(); slide.current?.cancel(); jelly.current?.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
